import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  COVENANT_METRICS, Covenant, CovenantComparator, CovenantMetric, CovenantReport, Facility,
} from '../../interfaces/borrowing.interface';
import { BorrowingService } from '../../services/borrowing.service';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useToast } from '../../context/ToastContext';
import { toLocalIsoDate } from '../../utils/dateUtils';
import { humanizeEnum } from '../../utils/format';

const Covenants: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!;
  const canManage = usePermission(PERMISSIONS.FINANCING_MANAGE);
  const [covenants, setCovenants] = useState<Covenant[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [report, setReport] = useState<CovenantReport | null>(null);
  const [asOf, setAsOf] = useState(toLocalIsoDate());
  const [form, setForm] = useState({ metric: 'CURRENT_RATIO', comparator: 'GTE', threshold: '', facilityId: '', note: '' });

  const load = useCallback(async () => {
    try {
      const [c, f] = await Promise.all([BorrowingService.listCovenants(institutionId), BorrowingService.listFacilities(institutionId)]);
      setCovenants(c); setFacilities(f);
    } catch (err) {
      toast.showApiError(err, 'Could not load covenants');
    }
  }, [institutionId, toast]);

  useEffect(() => { load(); }, [load]);

  const evaluate = async () => {
    try { setReport(await BorrowingService.evaluateCovenants(institutionId, asOf)); }
    catch (err) { toast.showApiError(err, 'Could not evaluate covenants'); }
  };

  const add = async () => {
    try {
      await BorrowingService.createCovenant({
        institutionId, metric: form.metric as CovenantMetric, comparator: form.comparator as CovenantComparator,
        threshold: Number(form.threshold), facilityId: form.facilityId || null, note: form.note || null, active: true,
      });
      toast.showSuccess('Covenant added');
      setForm(s => ({ ...s, threshold: '', note: '' }));
      await load();
    } catch (err) {
      toast.showApiError(err, 'Could not add the covenant');
    }
  };

  const remove = async (id: string) => {
    try { await BorrowingService.deleteCovenant(id); toast.showSuccess('Covenant removed'); await load(); }
    catch (err) { toast.showApiError(err, 'Could not remove the covenant'); }
  };

  const color = (r: string) => (r === 'PASS' ? 'success' : r === 'BREACH' ? 'error' : 'default');

  return (
    <Box data-help-page="financing.covenants.page" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flex: '1 1 240px' }}>Loan covenants</Typography>
        <Button component={RouterLink} to="/financing">Facilities</Button>
      </Box>

      {canManage && (
        <Paper data-help-section="financing.covenants.form" sx={{ p: 2, mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <TextField data-help="financing.covenants.metric" sx={{ flex: '1 1 180px', maxWidth: 240 }} size="small" select label="Metric" value={form.metric} onChange={e => setForm(s => ({ ...s, metric: e.target.value }))}>
            {COVENANT_METRICS.map(m => <MenuItem key={m} value={m}>{humanizeEnum(m)}</MenuItem>)}
          </TextField>
          <TextField data-help="financing.covenants.comparator" sx={{ flex: '1 1 140px', maxWidth: 180 }} size="small" select label="Must be" value={form.comparator} onChange={e => setForm(s => ({ ...s, comparator: e.target.value }))}>
            <MenuItem value="GTE">At least</MenuItem><MenuItem value="LTE">At most</MenuItem>
          </TextField>
          <TextField data-help="financing.covenants.threshold" sx={{ flex: '1 1 120px', maxWidth: 160 }} size="small" type="number" label="Threshold" value={form.threshold} onChange={e => setForm(s => ({ ...s, threshold: e.target.value }))} />
          <TextField data-help="financing.covenants.facility" sx={{ flex: '1 1 200px', maxWidth: 280 }} size="small" select label="Facility" value={form.facilityId} onChange={e => setForm(s => ({ ...s, facilityId: e.target.value }))}>
            <MenuItem value=""><em>All (institution-wide)</em></MenuItem>
            {facilities.map(f => <MenuItem key={f.id} value={f.id}>{f.facilityNo} - {f.name}</MenuItem>)}
          </TextField>
          <Button data-help="financing.covenants.add" variant="contained" disabled={form.threshold === ''} onClick={add}>Add covenant</Button>
        </Paper>
      )}

      <TableContainer data-help-section="financing.covenants.table" component={Paper} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead><TableRow><TableCell data-help="financing.covenants.col.metric">Metric</TableCell><TableCell data-help="financing.covenants.col.rule">Rule</TableCell><TableCell data-help="financing.covenants.col.facility">Facility</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {covenants.map(c => (
              <TableRow key={c.id}><TableCell>{humanizeEnum(c.metric)}</TableCell>
                <TableCell>{c.comparator === 'GTE' ? 'at least' : 'at most'} {c.threshold}</TableCell><TableCell>{c.facilityNo ?? 'All'}</TableCell>
                <TableCell align="right">{canManage && <Button data-help="financing.covenants.action.remove" size="small" color="error" onClick={() => remove(c.id)}>Remove</Button>}</TableCell></TableRow>
            ))}
            {covenants.length === 0 && <TableRow><TableCell colSpan={4}>No covenants defined.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>

      <Box data-help-section="financing.covenants.evaluate" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
        <TextField data-help="financing.covenants.asOf" sx={{ flex: '1 1 160px', maxWidth: 200 }} size="small" type="date" label="As of" InputLabelProps={{ shrink: true }} value={asOf} onChange={e => setAsOf(e.target.value)} />
        <Button data-help="financing.covenants.evaluateBtn" variant="outlined" onClick={evaluate}>Evaluate</Button>
      </Box>
      {report && (
        report.results.length === 0 ? <Alert severity="info">Nothing to evaluate.</Alert> : (
          <TableContainer data-help-section="financing.covenants.results" component={Paper}><Table size="small">
            <TableHead><TableRow><TableCell data-help="financing.covenants.col.metric">Metric</TableCell><TableCell data-help="financing.covenants.col.facility">Facility</TableCell><TableCell data-help="financing.covenants.col.actual" align="right">Actual</TableCell><TableCell data-help="financing.covenants.col.result">Result</TableCell><TableCell data-help="financing.covenants.col.detail">Detail</TableCell></TableRow></TableHead>
            <TableBody>
              {report.results.map(r => (
                <TableRow key={r.covenantId}><TableCell>{humanizeEnum(r.metric)}</TableCell><TableCell>{r.facilityNo ?? 'All'}</TableCell>
                  <TableCell align="right">{r.actual ?? '-'}</TableCell>
                  <TableCell><Chip size="small" color={color(r.result)} label={r.result} /></TableCell><TableCell>{r.detail}</TableCell></TableRow>
              ))}
            </TableBody></Table></TableContainer>
        )
      )}
    </Box>
  );
};

export default Covenants;
